import React from 'react';
import p5 from 'p5';
import BaseSimulation from './BaseSimulation';

/**
 * Tax Optimization Simulation - CloudTech Global Inc.
 * 
 * Case Study Context:
 * - Global software company, $2.4B revenue, 45 countries, 12 entities
 * - Before: 28.5% ETR, $684M annual tax
 * - After: 23.2% ETR, $557M annual tax
 * - MINLP optimization with transfer pricing, BEPS compliance
 * - Savings: $127M annually, $1.02B NPV over 10 years
 * 
 * Visualization:
 * - Global profit allocation across key jurisdictions
 * - Tax rate comparison by country
 * - Transfer pricing flows (IP royalties)
 * - ETR (Effective Tax Rate) optimization
 * - BEPS substance score
 * - Before/after comparison
 */

const TaxSimulation: React.FC = () => {
  const sketch = (p: p5) => {
    interface Entity {
      name: string;
      country: string;
      taxRate: number;
      profitBefore: number;
      profitAfter: number;
      x: number;
      y: number;
      color: p5.Color;
    }
    
    let entities: Entity[] = [];
    let optimized = false;
    let paused = false;
    let speed = 1;
    let selectedEntity = 0;
    let time = 0;
    
    const totalRevenue = 2400; // $2.4B
    
    p.setup = () => {
      p.createCanvas(800, 500);
      p.frameRate(30);
      
      // Initialize key entities from case study
      entities = [
        {
          name: 'US Parent',
          country: 'United States',
          taxRate: 21,
          profitBefore: 800, // $800M
          profitAfter: 620,  // Optimized: $620M
          x: 150,
          y: 200,
          color: p.color(255, 150, 150)
        },
        {
          name: 'Ireland IP',
          country: 'Ireland',
          taxRate: 12.5,
          profitBefore: 400,
          profitAfter: 580, // Increased by $180M
          x: 300,
          y: 150,
          color: p.color(150, 255, 150)
        },
        {
          name: 'Singapore HQ',
          country: 'Singapore',
          taxRate: 17,
          profitBefore: 300,
          profitAfter: 395, // Increased by $95M
          x: 500,
          y: 200,
          color: p.color(150, 200, 255)
        },
        {
          name: 'UK Sales',
          country: 'United Kingdom',
          taxRate: 19,
          profitBefore: 250,
          profitAfter: 220,
          x: 280,
          y: 280,
          color: p.color(255, 200, 150)
        },
        {
          name: 'Germany Ops',
          country: 'Germany',
          taxRate: 30,
          profitBefore: 200,
          profitAfter: 170,
          x: 350,
          y: 300,
          color: p.color(255, 180, 180)
        },
        {
          name: 'Netherlands',
          country: 'Netherlands',
          taxRate: 25,
          profitBefore: 180,
          profitAfter: 160,
          x: 320,
          y: 220,
          color: p.color(200, 200, 255)
        },
        {
          name: 'Japan Sales',
          country: 'Japan',
          taxRate: 30,
          profitBefore: 150,
          profitAfter: 135,
          x: 600,
          y: 180,
          color: p.color(255, 200, 200)
        },
        {
          name: 'Others',
          country: 'Various',
          taxRate: 25,
          profitBefore: 120,
          profitAfter: 120,
          x: 450,
          y: 350,
          color: p.color(200, 200, 200)
        }
      ];
    };
    
    p.mousePressed = () => {
      if (p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height) {
        // Check if clicking on entity
        for (let i = 0; i < entities.length; i++) {
          const e = entities[i];
          const d = p.dist(p.mouseX, p.mouseY, e.x, e.y);
          if (d < 30) {
            selectedEntity = i;
            return false;
          }
        }
        
        // Check optimization button
        if (p.mouseX > 650 && p.mouseX < 780 && p.mouseY > 60 && p.mouseY < 90) {
          optimized = !optimized;
          return false;
        }
        
        paused = !paused;
        return false;
      }
    };
    
    p.keyPressed = () => {
      if (p.key === ' ') paused = !paused;
      if (p.key === 'r' || p.key === 'R') {
        optimized = false;
        time = 0;
      }
      if (p.key === '+' || p.key === '=') speed = Math.min(speed + 0.5, 3);
      if (p.key === '-' || p.key === '_') speed = Math.max(speed - 0.5, 0.5);
      if (p.key === 'o' || p.key === 'O') optimized = !optimized;
      if (p.key >= '1' && p.key <= '8') {
        selectedEntity = parseInt(p.key) - 1;
      }
    };
    
    p.draw = () => {
      p.background(250);
      
      // Update simulation
      if (!paused && p.frameCount % Math.floor(30 / speed) === 0) {
        time++;
      }
      
      // Draw visualization
      drawTitle();
      drawGlobalMap();
      drawTransferPricing();
      drawTaxComparison();
      drawMetrics();
      drawControls();
    };
    
    const drawTitle = () => {
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(14);
      p.text('CloudTech Global Inc. - Tax Optimization', 20, 15);
      
      p.textSize(11);
      p.fill(100);
      p.text('MINLP Optimization | 12 Entities | 45 Countries | BEPS Compliant', 20, 35);
      
      // Optimization toggle button
      const btnX = 650;
      const btnY = 60;
      const btnW = 130;
      const btnH = 30;
      
      p.fill(optimized ? p.color(100, 200, 100) : p.color(255, 200, 100));
      p.stroke(100);
      p.strokeWeight(1);
      p.rect(btnX, btnY, btnW, btnH, 5);
      
      p.fill(40);
      p.noStroke();
      p.textAlign(p.CENTER, p.CENTER);
      p.textSize(11);
      p.text(optimized ? '✓ Optimized' : 'Before Optimization', btnX + btnW / 2, btnY + btnH / 2);
    };
    
    const drawGlobalMap = () => {
      const mapX = 50;
      const mapY = 100;
      const mapW = 600;
      const mapH = 300;
      
      // Background
      p.fill(245);
      p.stroke(200);
      p.rect(mapX, mapY, mapW, mapH);
      
      // Title
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(12);
      p.text('Global Profit Allocation', mapX + 10, mapY - 20);
      
      // Draw transfer pricing flows (if optimized)
      if (optimized) {
        // IP royalty flows to Ireland
        p.stroke(100, 200, 100, 100);
        p.strokeWeight(2);
        
        entities.forEach((e, i) => {
          if (i !== 1 && i < 7) { // Flow to Ireland IP from major entities
            const flowAmount = (e.profitBefore - e.profitAfter) * 0.5;
            if (flowAmount > 0) {
              p.line(e.x, e.y, entities[1].x, entities[1].y);
              
              // Arrow
              const angle = Math.atan2(entities[1].y - e.y, entities[1].x - e.x);
              const arrowSize = 8;
              p.push();
              p.translate(entities[1].x, entities[1].y);
              p.rotate(angle);
              p.fill(100, 200, 100, 100);
              p.noStroke();
              p.triangle(-arrowSize, -arrowSize/2, -arrowSize, arrowSize/2, 0, 0);
              p.pop();
            }
          }
        });
      }
      
      // Draw entities
      entities.forEach((e, i) => {
        const isSelected = i === selectedEntity;
        const profit = optimized ? e.profitAfter : e.profitBefore;
        const size = 20 + (profit / 800) * 40; // Size based on profit
        
        // Circle
        p.fill(e.color);
        p.stroke(isSelected ? 255 : 100);
        p.strokeWeight(isSelected ? 3 : 1);
        p.circle(e.x, e.y, size);
        
        // Label
        p.fill(40);
        p.noStroke();
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(9);
        p.text(e.name, e.x, e.y + size / 2 + 3);
        
        // Profit amount
        p.textSize(8);
        p.text(`$${profit.toFixed(0)}M`, e.x, e.y - 2);
        
        // Tax rate
        p.fill(100);
        p.textSize(7);
        p.text(`${e.taxRate}%`, e.x, e.y + 8);
      });
    };
    
    const drawTransferPricing = () => {
      const chartX = 680;
      const chartY = 120;
      const chartW = 110;
      const chartH = 200;
      
      // Background
      p.fill(255);
      p.stroke(200);
      p.rect(chartX, chartY, chartW, chartH);
      
      // Title
      p.fill(40);
      p.noStroke();
      p.textAlign(p.CENTER, p.TOP);
      p.textSize(10);
      p.text('Transfer Pricing', chartX + chartW / 2, chartY + 5);
      
      if (optimized) {
        p.textSize(8);
        p.fill(100);
        p.textAlign(p.LEFT, p.TOP);
        let y = chartY + 25;
        
        p.text('IP Royalties:', chartX + 5, y);
        y += 12;
        
        // Key flows
        const flows = [
          {from: 'US → Ireland', amount: 90},
          {from: 'UK → Ireland', amount: 30},
          {from: 'Germany → Ireland', amount: 30},
          {from: 'Singapore → Ireland', amount: 30}
        ];
        
        flows.forEach(flow => {
          p.fill(100, 180, 100);
          p.text(`${flow.from}`, chartX + 5, y);
          y += 10;
          p.fill(40);
          p.text(`$${flow.amount}M`, chartX + 5, y);
          y += 15;
        });
        
        y += 10;
        p.fill(100);
        p.text('Arm\'s Length:', chartX + 5, y);
        y += 10;
        p.fill(100, 180, 100);
        p.text('✓ 8-15% range', chartX + 5, y);
        
        y += 20;
        p.fill(100);
        p.text('BEPS Substance:', chartX + 5, y);
        y += 10;
        p.fill(100, 180, 100);
        p.text('✓ Score: 0.89', chartX + 5, y);
      } else {
        p.fill(100);
        p.textSize(8);
        p.textAlign(p.CENTER, p.CENTER);
        p.text('Suboptimal\nallocation', chartX + chartW / 2, chartY + chartH / 2);
      }
    };
    
    const drawTaxComparison = () => {
      const chartX = 50;
      const chartY = 420;
      const chartW = 350;
      const chartH = 60;
      
      // Calculate total tax
      let taxBefore = 0;
      let taxAfter = 0;
      
      entities.forEach(e => {
        taxBefore += e.profitBefore * (e.taxRate / 100);
        taxAfter += e.profitAfter * (e.taxRate / 100);
      });
      
      const etrBefore = (taxBefore / totalRevenue) * 100;
      const etrAfter = (taxAfter / totalRevenue) * 100;
      
      // Background
      p.fill(255);
      p.stroke(200);
      p.rect(chartX, chartY, chartW, chartH);
      
      // Title
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      p.text('Global Tax Comparison', chartX + 10, chartY - 18);
      
      // Before bar
      const barY = chartY + 15;
      const barH = 18;
      const maxTax = 700;
      
      const beforeW = (taxBefore / maxTax) * (chartW - 100);
      p.fill(255, 150, 150);
      p.noStroke();
      p.rect(chartX + 80, barY, beforeW, barH);
      
      p.fill(40);
      p.textAlign(p.RIGHT, p.CENTER);
      p.textSize(9);
      p.text('Before:', chartX + 75, barY + barH / 2);
      
      p.textAlign(p.LEFT, p.CENTER);
      p.text(`$${taxBefore.toFixed(0)}M (${etrBefore.toFixed(1)}% ETR)`, chartX + 85 + beforeW, barY + barH / 2);
      
      // After bar
      const afterW = (taxAfter / maxTax) * (chartW - 100);
      p.fill(150, 255, 150);
      p.rect(chartX + 80, barY + barH + 8, afterW, barH);
      
      p.fill(40);
      p.textAlign(p.RIGHT, p.CENTER);
      p.text('After:', chartX + 75, barY + barH + 8 + barH / 2);
      
      p.textAlign(p.LEFT, p.CENTER);
      p.text(`$${taxAfter.toFixed(0)}M (${etrAfter.toFixed(1)}% ETR)`, chartX + 85 + afterW, barY + barH + 8 + barH / 2);
    };
    
    const drawMetrics = () => {
      const metricsY = 420;
      const metricsX = 430;
      
      // Calculate metrics
      let taxBefore = 0;
      let taxAfter = 0;
      let profitBefore = 0;
      let profitAfter = 0;
      
      entities.forEach(e => {
        taxBefore += e.profitBefore * (e.taxRate / 100);
        taxAfter += e.profitAfter * (e.taxRate / 100);
        profitBefore += e.profitBefore;
        profitAfter += e.profitAfter;
      });
      
      const etrBefore = (taxBefore / totalRevenue) * 100;
      const etrAfter = (taxAfter / totalRevenue) * 100;
      const savings = taxBefore - taxAfter;
      const npvSavings = savings * 8; // Simplified 10-year NPV
      
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      
      p.text('Optimization Results', metricsX, metricsY);
      
      p.textSize(10);
      let y = metricsY + 20;
      const lineH = 16;
      
      p.fill(100);
      p.text('Annual Tax Savings:', metricsX, y);
      p.fill(100, 180, 100);
      p.text(`$${savings.toFixed(0)}M`, metricsX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('ETR Reduction:', metricsX, y);
      p.fill(100, 180, 100);
      p.text(`${(etrBefore - etrAfter).toFixed(1)}%`, metricsX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('10-Year NPV:', metricsX, y);
      p.fill(100, 180, 100);
      p.text(`$${(npvSavings / 1000).toFixed(2)}B`, metricsX + 150, y);
    };
    
    const drawControls = () => {
      p.fill(100);
      p.noStroke();
      p.textAlign(p.LEFT, p.BOTTOM);
      p.textSize(9);
      
      p.text('Click Entity: Select | O: Toggle Optimization | Space: Pause | R: Reset | +/-: Speed | 1-8: Select Entity', 20, 490);
      
      p.textAlign(p.RIGHT, p.BOTTOM);
      p.fill(paused ? 200 : 100);
      p.text(paused ? 'PAUSED' : `RUNNING (${speed.toFixed(1)}x)`, 780, 490);
    };
  };

  return (
    <BaseSimulation
      sketch={sketch}
      title="Tax Optimization Simulation"
      description="Interactive visualization of CloudTech Global's tax optimization using Mixed-Integer Nonlinear Programming (MINLP). Shows global profit allocation, transfer pricing, and ETR reduction from 28.5% to 23.2%."
    />
  );
};

export default TaxSimulation;
